"use client";

import React, { useEffect, useState } from "react";
import { AlertTriangle, Info, Users } from "lucide-react";
import Drill from "@drillflo/drillcore/lib/Drill";
import Performer from "@drillflo/drillcore/lib/Performer";
import { PlaybackData } from "../hooks/usePlayback";

interface PerformerStepSize {
  performer: Performer;
  stepSize: {
    statement: string;
    stepSizeInSteps: number;
    stepSizeInInches: number;
    stepSizeInYards: number;
    xTo5: number;
    isTooLarge: boolean;
  };
}

function calculateStepSize(
  point1HorizontalStepsFrom50: number,
  point1VerticalStepsFromCenter: number,
  point2HorizontalStepsFrom50: number,
  point2VerticalStepsFromCenter: number,
  counts: number
) {
  const dx = point2HorizontalStepsFrom50 - point1HorizontalStepsFrom50;
  const dy = point2VerticalStepsFromCenter - point1VerticalStepsFromCenter;

  // Total physical steps taken on the field
  const totalSteps = Math.sqrt(dx * dx + dy * dy);

  // Step size (in "steps") per count
  const stepSizeInSteps = totalSteps / counts;
  // Now convert to step size in inches (1 step = 22.5 inches)
  const stepSizeInInches = stepSizeInSteps * 22.5;
  // Convert inches to yards
  const stepSizeInYards = stepSizeInInches / 36;
  // Get the X in "X to 5"
  const xTo5 = stepSizeInYards > 0 ? 5 / stepSizeInYards : 0;

  const isTooLarge = stepSizeInInches > 45 ? true : false;

  return {
    statement: `${xTo5.toFixed(1)} to 5`,
    stepSizeInSteps,
    stepSizeInInches,
    stepSizeInYards,
    xTo5,
    isTooLarge,
  };
}

export default function PageAnalysis({
  drill,
  playbackState,
}: {
  drill: Drill;
  playbackState: PlaybackData;
}) {
  const [pagePerformerStepSizes, setPagePerformerStepSizes] = useState<
    PerformerStepSize[]
  >([]);
  const [
    showTooLargeStepSizeNotification,
    setShowTooLargeStepSizeNotification,
  ] = useState<PerformerStepSize[]>([]);

  const [showOnlyTooLarge, setShowOnlyTooLarge] = useState(false);

  useEffect(() => {
    if (
      drill &&
      drill.performers &&
      drill.pages &&
      drill.pages.length > 1 &&
      drill?.selectedPageIndex > 0 &&
      drill.selectedPageIndex
    ) {
      const currentPage = drill.pages[drill.selectedPageIndex];
      const previousPage = drill.pages[drill.selectedPageIndex - 1];
      const performerStepSizes = [];

      for (const performer of drill.performers) {
        const performerCurrentPosition =
          drill.pages[drill.selectedPageIndex].performerPositions.get(
            performer
          );
        const performerPreviousPosition =
          drill.pages[drill.selectedPageIndex - 1].performerPositions.get(
            performer
          );

        if (!performerCurrentPosition || !performerPreviousPosition) {
          continue;
        }

        const stepSize = calculateStepSize(
          performerCurrentPosition.horizontalStepsFrom50 || 0,
          performerCurrentPosition.verticalStepsFromCenter || 0,
          performerPreviousPosition.horizontalStepsFrom50 || 0,
          performerPreviousPosition.verticalStepsFromCenter || 0,
          drill.selectedPage.counts
        );
        performerStepSizes.push({ performer, stepSize });
      }
      setPagePerformerStepSizes(performerStepSizes);

      const performersTooLargeSteps = performerStepSizes.filter(
        (performer) => performer.stepSize.isTooLarge
      );

      setShowTooLargeStepSizeNotification(performersTooLargeSteps);
    } else {
      setPagePerformerStepSizes([]);
    }
  }, [drill, playbackState]);

  if (!pagePerformerStepSizes || pagePerformerStepSizes.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 p-4 border-b bg-gray-50">
          <Users className="w-4 h-4 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Page Analysis</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No analysis data available</p>
            <p className="text-xs text-gray-400 mt-1">
              Select a page to view step size analysis
            </p>
          </div>
        </div>
      </div>
    );
  }

  const filteredStepSizes = showOnlyTooLarge
    ? pagePerformerStepSizes.filter((p: any) => p.stepSize.isTooLarge)
    : pagePerformerStepSizes;

  const largest = [...pagePerformerStepSizes].sort(
    (a, b) => b.stepSize.stepSizeInInches - a.stepSize.stepSizeInInches
  )[0];

  const smallest = [...pagePerformerStepSizes].sort(
    (a, b) => a.stepSize.stepSizeInInches - b.stepSize.stepSizeInInches
  )[0];

  const unsafeCount = pagePerformerStepSizes.filter(
    (p: any) => p.stepSize.isTooLarge
  ).length;

  return (
    <div className="h-full flex flex-col bg-white">
      {showTooLargeStepSizeNotification.length > 0 && (
        <div
          className={`absolute bottom-auto left-auto right-0 top-0 z-10 inline-block translate-y-6 -translate-x-1 rotate-0 skew-x-0 skew-y-0 scale-x-100 scale-y-100 rounded-full bg-red-600 p-1 transition-all duration-[2s] ${
            showTooLargeStepSizeNotification.length > 1
              ? "opacity-100"
              : "opacity-0"
          }`}
        ></div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b bg-gray-50">
        <Users className="w-4 h-4 text-gray-600" />
        <h3 className="font-semibold text-gray-900">Page Analysis</h3>
        <div className="relative group">
          <Info className="w-4 h-4 text-gray-400 cursor-help" />
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-100">
            Step size analysis for each performer. Large steps may be unsafe or
            too difficult to march effectively.
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-4 space-y-3 border-b bg-gray-50/50">
        <div className="grid grid-cols-1 gap-2">
          <div className="bg-white rounded-lg p-3 border">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">
                Largest Step
              </span>
              {largest?.stepSize?.isTooLarge && (
                <AlertTriangle className="w-3 h-3 text-red-500" />
              )}
            </div>
            <div
              className={`text-sm font-semibold mt-1 ${
                largest?.stepSize?.isTooLarge ? "text-red-600" : "text-gray-900"
              }`}
            >
              {largest?.stepSize?.statement || "N/A"}
            </div>
          </div>
        </div>

        {unsafeCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-800">
                {unsafeCount} unsafe performer step size
                {unsafeCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Filter Controls */}
      <div className="p-4 border-b">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyTooLarge}
            onChange={(e) => setShowOnlyTooLarge(e.target.checked)}
            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
          />
          <span className="text-sm text-gray-700 select-none">
            Show only unsafe step sizes
          </span>
          {unsafeCount > 0 && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
              {unsafeCount}
            </span>
          )}
        </label>
      </div>

      {/* Data Table */}
      <div className="flex-1 overflow-hidden max-h-96 h-full">
        <div className="h-full overflow-y-scroll max-h-72 h-full">
          <table className="w-full  max-h-72 h-full">
            <thead className="sticky top-0 bg-white border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Step Size
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Inches
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStepSizes
                .sort((a: any, b: any) =>
                  a.performer.name.localeCompare(b.performer.name)
                )
                .map((performerStepSize: any) => (
                  <tr
                    key={performerStepSize.performer.id}
                    className={`hover:bg-gray-50 ${
                      performerStepSize.stepSize.isTooLarge ? "bg-red-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {performerStepSize.stepSize.isTooLarge && (
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            performerStepSize.stepSize.isTooLarge
                              ? "text-red-900"
                              : "text-gray-900"
                          }`}
                        >
                          {performerStepSize.performer.name}
                        </span>
                      </div>
                    </td>
                    <td
                      className={`px-4 py-3 text-sm ${
                        performerStepSize.stepSize.isTooLarge
                          ? "text-red-700"
                          : "text-gray-600"
                      }`}
                    >
                      {performerStepSize.stepSize.statement}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm text-right font-mono ${
                        performerStepSize.stepSize.isTooLarge
                          ? "text-red-700"
                          : "text-gray-600"
                      }`}
                    >
                      {performerStepSize.stepSize.stepSizeInInches.toFixed(2)}"
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {filteredStepSizes.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-gray-500">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No unsafe step sizes found</p>
                <p className="text-xs text-gray-400 mt-1">
                  All performers have safe step sizes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
